import {
  addDays,
  differenceInDays,
  differenceInMonths,
  differenceInWeeks,
  getYear,
  setYear,
  subDays,
  subMonths,
  subWeeks,
  subYears,
} from "date-fns";
import moment from "moment";

import { type User } from "@internal/user";
import { constants } from "@north-beam/nb-common";
import {
  type DateInterval,
  type ITimeGranularityV2,
} from "@north-beam/nb-common";

import { getDateOnly } from "./chart";

export const getTimeGranularityDateFormat = (
  timeGranularity: ITimeGranularityV2,
) => {
  switch (timeGranularity) {
    case "weekly":
      return "wo 'week of' YYYY";
    case "monthly":
      return "MMM YYYY";
    case "hourly":
      return "MMM D, h:mmA";
    case "daily":
    default:
      return "MMM D, YYYY";
  }
};

// TODO: Generalize and move me to constants.
export const deriveComparisonDateRange = (
  dateRange: DateInterval,
  compareDateRange: constants.CompareDateRange,
  timezone: User["timezone"],
  timeGranularity: ITimeGranularityV2,
): DateInterval | null => {
  const formatString = "YYYY-MM-DD";
  const startDate = getDateOnly(dateRange.startDate, timezone);
  const endDate = getDateOnly(dateRange.endDate, timezone);

  const daysDifference = differenceInDays(endDate, startDate);

  if (constants.isCompareDateRangeOfType(compareDateRange, "previousPeriod")) {
    const finalStartDate = moment
      .tz(startDate, timezone)
      .subtract(daysDifference + 1, "day")
      .format(formatString);
    const finalEndDate = moment
      .tz(startDate, timezone)
      .subtract(1, "day")
      .format(formatString);

    return {
      startDate: finalStartDate,
      endDate: finalEndDate,
    };
  } else if (
    constants.isCompareDateRangeOfType(compareDateRange, "lastPeriod")
  ) {
    if (timeGranularity === "monthly") {
      const diff = differenceInMonths(endDate, startDate) + 1;
      const newEndDate = subMonths(endDate, diff);
      const newStartDate = subMonths(startDate, diff);
      const finalStartDate = moment
        .tz(newStartDate, timezone)
        .format(formatString);
      const finalEndDate = moment.tz(newEndDate, timezone).format(formatString);

      return {
        startDate: finalStartDate,
        endDate: finalEndDate,
      };
    }
    if (timeGranularity === "weekly") {
      const diff = differenceInWeeks(endDate, startDate) + 1;
      const newEndDate = subWeeks(endDate, diff);
      const newStartDate = subWeeks(startDate, diff);

      const finalStartDate = moment
        .tz(newStartDate, timezone)
        .format(formatString);
      const finalEndDate = moment.tz(newEndDate, timezone).format(formatString);

      return {
        startDate: finalStartDate,
        endDate: finalEndDate,
      };
    }

    const newStartDate = subDays(startDate, daysDifference + 1);
    const newEndDate = subDays(startDate, 1);

    const finalStartDate = moment
      .tz(newStartDate, timezone)
      .format(formatString);
    const finalEndDate = moment.tz(newEndDate, timezone).format(formatString);

    return {
      startDate: finalStartDate,
      endDate: finalEndDate,
    };
  } else if (
    constants.isCompareDateRangeOfType(
      compareDateRange,
      "samePeriodDifferentYear",
    )
  ) {
    const diff = getYear(endDate) - getYear(startDate);
    const newStartDate = setYear(startDate, compareDateRange.year - diff);
    const newEndDate = addDays(newStartDate, daysDifference);

    const finalStartDate = moment
      .tz(newStartDate, timezone)
      .format(formatString);
    const finalEndDate = moment.tz(newEndDate, timezone).format(formatString);
    return {
      startDate: finalStartDate,
      endDate: finalEndDate,
    };
  } else if (
    constants.isCompareDateRangeOfType(compareDateRange, "samePeriodPriorYear")
  ) {
    const newStartDate = subYears(startDate, 1);
    const newEndDate = addDays(newStartDate, daysDifference);
    const finalStartDate = moment
      .tz(newStartDate, timezone)
      .format(formatString);
    const finalEndDate = moment.tz(newEndDate, timezone).format(formatString);
    return {
      startDate: finalStartDate,
      endDate: finalEndDate,
    };
  } else if (constants.isCompareDateRangeOfType(compareDateRange, "custom")) {
    const finalStartDate = moment
      .tz(getDateOnly(compareDateRange.startDate, timezone), timezone)
      .format(formatString);
    const finalEndDate = moment
      .tz(getDateOnly(compareDateRange.endDate, timezone), timezone)
      .format(formatString);

    return {
      startDate: finalStartDate,
      endDate: finalEndDate,
    };
  }
  /* TODO: We might want to throw an error here, since the typechecks of the compare date range
     type should result in this code never being reached (outside of a test). */
  return null;
};
